import React, { useState } from 'react';
import { leadApi } from '../services/api';
import { STAGES, STAGE_LABEL } from '../lib';

interface Props {
  leadId: string;
  current: string;
  crmLabel?: string | null;
  // The operator's read of the lead, held as the single-valued `interest:` tag.
  interest: string | null;
  onClose: () => void;
}

const INTEREST_LEVELS: [string, string][] = [['High', 'high'], ['Medium', 'medium'], ['Low', 'low']];

// The picker's sections — same grouping as the Nurture app's sheet.
const GROUPS: { title: string; stages: string[] }[] = [
  { title: 'On the way to a visit', stages: ['new', 'attempting', 'contacted', 'engaged', 'followup', 'visit_intent', 'visit_scheduled', 'visited'] },
  { title: 'Site team', stages: ['revisit_scheduled', 'revisited', 'negotiation', 'booked'] },
  { title: 'Waiting on the lead', stages: ['unreachable', 'gone_quiet', 'deferred'] },
  { title: 'Out', stages: ['lost', 'disqualified'] },
];
const EXITS = new Set(['lost', 'disqualified']);

// Drop reasons: the label the operator taps, and the tag it writes — the same
// vocabulary the Nurture app uses (DropReasons.kt), so drops slice together.
const DROP_REASONS: [string, string][] = [
  ['Not interested', 'disposition:not_interested'],
  ['Too far', 'objection:too_far_location'],
  ['Price', 'objection:price'],
  ['Agent / broker', 'lead_type:channel_partner'],
  ['Selling to us', 'lead_type:vendor_b2b'],
  ['Bought elsewhere', 'disposition:bought_elsewhere'],
  ['Denies enquiring', 'disposition:denies_inquiry'],
  ['Clicked by mistake', 'lead_type:accidental_click'],
  ['Not a prospect', 'lead_type:not_a_prospect'],
  ['Do not contact', 'disposition:do_not_contact'],
];

const StagePanel: React.FC<Props> = ({ leadId, current, crmLabel, interest: currentInterest, onClose }) => {
  const [selected, setSelected] = useState<string>(current);
  const [interest, setInterest] = useState<string | null>(currentInterest);
  const [reason, setReason] = useState('');
  const [tags, setTags] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const changed = selected !== current;
  const interestChanged = interest !== null && interest !== currentInterest;
  const needsReason = changed && EXITS.has(selected);
  const canSave = (changed || interestChanged) && !saving && (!needsReason || reason.trim() || tags.size > 0);

  const save = async () => {
    if (!canSave) return;
    setSaving(true); setError(null);
    try {
      const tagList = Array.from(tags);
      if (interestChanged) {
        await leadApi.mergeTags(leadId, { add: [{ tag: `interest:${interest}`, replace_dimension: true }] });
      }
      if (changed) await leadApi.update(leadId, {
        stage: selected,
        ...(needsReason ? { stage_reason: reason.trim() || undefined, stage_reason_tags: tagList } : {}),
      } as any);
      if (needsReason && tagList.length) {
        // The analytics tags, as the app writes them: the chip tag plus a
        // manual: shadow that survives every re-derive (an operator's drop is
        // a terminal fact).
        await leadApi.addTags(leadId, [...tagList, ...tagList.map(t => `manual:dropped_${t.split(':')[1]}`)]);
      }
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to save');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-charcoal/30 flex items-center justify-center z-50">
      <div className="bg-warm-white rounded-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="font-display text-2xl text-charcoal">Stage &amp; interest</h2>
          <button onClick={onClose} className="text-graphite hover:text-charcoal">✕</button>
        </div>

        {error && <div className="mb-3 p-2 bg-brick/10 text-brick text-sm rounded">{error}</div>}

        <div className="mb-4">
          <div className="text-xs text-graphite mb-1">Interest</div>
          <div className="flex flex-wrap gap-1.5">
            {INTEREST_LEVELS.map(([lbl, value]) => (
              <button key={value} type="button" className="pill" aria-pressed={interest === value}
                disabled={saving} onClick={() => { setInterest(value); setError(null); }}>
                {lbl}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-3">
          {GROUPS.map(g => (
            <div key={g.title}>
              <div className="text-xs text-graphite mb-1">{g.title}</div>
              <div className="flex flex-wrap gap-1.5">
                {g.stages.filter(s => STAGES.includes(s as any)).map(s => (
                  <button
                    key={s}
                    type="button"
                    disabled={saving}
                    onClick={() => { setSelected(s); setError(null); }}
                    className={`px-2.5 py-1 rounded-full border text-sm ${selected === s ? 'bg-forest text-warm-white border-forest' : 'border-sketch text-charcoal hover:border-forest'}`}
                  >
                    {STAGE_LABEL[s]}{s === current ? ' · current' : ''}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        {needsReason && (
          <div className="mt-4">
            <div className="text-xs text-graphite mb-1">Why? Tap what fits:</div>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {DROP_REASONS.map(([lbl, tag]) => (
                <button
                  key={tag}
                  type="button"
                  disabled={saving}
                  onClick={() => setTags(prev => { const n = new Set(prev); n.has(tag) ? n.delete(tag) : n.add(tag); return n; })}
                  className={`px-2.5 py-1 rounded-full border text-sm ${tags.has(tag) ? 'bg-brick/10 text-brick border-brick/40' : 'border-sketch text-charcoal hover:border-brick/40'}`}
                >
                  {lbl}
                </button>
              ))}
            </div>
            <input
              id="stage-reason"
              className="inp w-full"
              placeholder="Anything else? (optional with a tag)"
              value={reason}
              onChange={e => setReason(e.target.value)}
              disabled={saving}
            />
          </div>
        )}

        {crmLabel && <p className="text-xs text-graphite mt-4">Sell.do: {crmLabel}</p>}

        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 border border-sketch rounded-lg text-sm">Cancel</button>
          <button onClick={save} disabled={!canSave} className="px-4 py-2 bg-forest text-warm-white rounded-lg text-sm disabled:opacity-50">
            {saving ? 'Saving…' : changed && interestChanged ? 'Update stage & interest' : interestChanged ? 'Update interest' : 'Update stage'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StagePanel;
